import { useCallback, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { Gtm } from '@/domains/core/tracking/utils/Gtm';
import { useFingerprint } from '@/domains/productDiscovery/fingerprint/hooks/useFingerprint';
import type { Product } from '@/domains/productDiscovery/Recommendation/interfaces/product';
import { usePlaylistReconciliation } from '@/domains/productDiscovery/Recommendation/modules/ProductRecommendation/hooks/usePlaylistReconciliation';

import type { IProduct } from '../../context/ProductBundle/productBundle.interfaces';
import {
  clickProductInBundleGtmEvent,
  viewBundleGtmEvent,
} from '../../utils/mappers';
import {
  PLAYLIST_LOCATION,
  type TPlaylistReconciliationData,
} from './useBundleTracking.interfaces';
import { useTrackingCustomization } from './useBundleTrackingCustomization';

export const useBundleTracking = (
  productList: Array<IProduct>,
  recoEngineName = '',
) => {
  const [viewEventSent, setViewEventSent] = useState<boolean>(false);

  const [
    { playlistFingerprint, playlistReconciliation },
    setPlaylistReconciliationData,
  ] = useState<TPlaylistReconciliationData>({
    playlistFingerprint: '',
    playlistReconciliation: '',
  });

  const { ref, inView: inScreen } = useInView({
    delay: 1000,
    threshold: 0.5,
    skip: !productList.length,
  });

  const { createPlaylistReconciliation, persistPlaylistReconciliation } =
    usePlaylistReconciliation();

  const listingFingerprint = useFingerprint();
  const trackingCustomization = useTrackingCustomization();

  useEffect(() => {
    if (inScreen && !viewEventSent && productList.length && !!ref) {
      setViewEventSent(true);

      createPlaylistReconciliation(
        recoEngineName,
        PLAYLIST_LOCATION.B2C_PRODUCT_PAGE,
      ).then((generatedReconciliation) => {
        setPlaylistReconciliationData(generatedReconciliation);

        const viewEvent = viewBundleGtmEvent(
          productList,
          listingFingerprint,
          PLAYLIST_LOCATION.B2C_PRODUCT_PAGE,
          recoEngineName,
          generatedReconciliation.playlistFingerprint,
          generatedReconciliation.playlistReconciliation,
        );

        trackingCustomization.getView().then((view) => {
          Gtm.push({ ...viewEvent, ...view });
        });
      });
    }
  }, [
    createPlaylistReconciliation,
    inScreen,
    listingFingerprint,
    productList,
    recoEngineName,
    ref,
    trackingCustomization,
    viewEventSent,
  ]);

  const handleProductCardClick = useCallback(
    async (articleId: Product['articleId']) => {
      const index = productList.findIndex(
        (product) => product.articleId === articleId,
      );
      const clickEvent = clickProductInBundleGtmEvent(
        productList,
        index,
        playlistFingerprint,
        playlistReconciliation,
        listingFingerprint,
        recoEngineName,
      );

      Gtm.push({
        ...clickEvent,
        ...(await trackingCustomization.getProductClick()),
      });

      persistPlaylistReconciliation(
        playlistFingerprint,
        playlistReconciliation,
        productList[index] ? productList[index].productId : 'undefined',
      );
    },
    [
      listingFingerprint,
      persistPlaylistReconciliation,
      playlistFingerprint,
      playlistReconciliation,
      productList,
      recoEngineName,
      trackingCustomization,
    ],
  );

  return {
    ref,
    handleProductCardClick,
    playlistFingerprint,
    playlistReconciliation,
  };
};
